import { useState } from 'react';

import { Stack } from '@mui/material';

import { useGetTypicalMonthsQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  DayTypeSetting,
  resetAnalysisData,
  selectAnalysisEndDate,
  selectAnalysisIsSaving,
  selectAnalysisName,
  selectAnalysisSegments,
  selectAnalysisStartDate,
  selectAnalysisStatus,
  selectDayTypeSetting,
  selectODUIMode,
} from 'state/originDestinationSlices';
import { selectProject, setTypicalMonth } from 'state/workflowSlice';

import { ACTIVE_STATUSES, MIN_OD_SEGMENTS, ODUIMode } from 'appConstants';
import { StlButton } from 'features/common/StlButton/StlButton';
import HexSpinner from 'features/spinner/HexSpinner';

import ODCreatePopup from '../ODCreatePopup';
import { useUnavailableODSegments } from '../hooks/useUnavailableODSegments';

import styles from './ODSidebar.module.css';

interface IAnalysisCreateButtons {
  disableRun?: boolean;
  disableClear?: boolean;
}

export default function AnalysisCreateButtons({
  disableRun = false,
  disableClear = false,
}: IAnalysisCreateButtons) {
  const dispatch = useAppDispatch();

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const analysisIsSaving = useAppSelector(selectAnalysisIsSaving);
  const analysisName = useAppSelector(selectAnalysisName);
  const project = useAppSelector(selectProject);
  const analysisStatus = useAppSelector(selectAnalysisStatus);
  const analysisSegments = useAppSelector(selectAnalysisSegments);
  const uiMode = useAppSelector(selectODUIMode);
  const analysisEndDate = useAppSelector(selectAnalysisEndDate);
  const analysisStartDate = useAppSelector(selectAnalysisStartDate);
  const dayTypeSetting = useAppSelector(selectDayTypeSetting);

  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project);
  const { hasUnavailableSegments, isValidatingSegments } =
    useUnavailableODSegments();

  const shouldShowLoading =
    analysisIsSaving ||
    (ACTIVE_STATUSES.includes(analysisStatus) && uiMode === ODUIMode.View);

  const clearForm = () => {
    dispatch(resetAnalysisData());
    if (dayTypeSetting === DayTypeSetting.BY_DAY_TYPE) {
      dispatch(setTypicalMonth(typicalMonthsInfo[0].typicalMonth));
    }
  };

  const handleClick = () => {
    if (uiMode === ODUIMode.Create) setShowCreateModal(true);
  };

  const hasData = Boolean(analysisName || analysisStartDate);
  const hasAllData =
    analysisName &&
    analysisStartDate &&
    analysisSegments?.length >= MIN_OD_SEGMENTS &&
    analysisEndDate;

  const runAnalysisActive = Boolean(
    !analysisIsSaving &&
      hasAllData &&
      !disableRun &&
      !hasUnavailableSegments &&
      !isValidatingSegments,
  );
  const clearActive =
    !disableClear && !analysisIsSaving && hasData && !shouldShowLoading;

  return (
    <>
      <Stack className={styles.stack_buttons}>
        <StlButton
          value="Clear"
          isActive={clearActive}
          handleClick={clearForm}
          width={122}
          variant="outlined"
        />
        <StlButton
          isActive={runAnalysisActive}
          icon={shouldShowLoading && <HexSpinner size={16} />}
          value={shouldShowLoading ? 'Running' : 'Run Analysis'}
          sx={
            shouldShowLoading && {
              backgroundColor: '#4D4D4D',
              color: '#fff !important',
            }
          }
          handleClick={handleClick}
          width={122}
        />
      </Stack>
      <ODCreatePopup open={showCreateModal} setOpen={setShowCreateModal} />
    </>
  );
}
